import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Button, Checkbox, Container, Dialog, FormControlLabel, FormHelperText, Stack, TextField } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { endOfDay } from 'date-fns';
import { FieldError } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { ZodIssueCode } from 'zod';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { toDate } from './FollowUpActionForm';
import { FollowUpActionSavedGallery } from './FollowUpActionSavedGallery';
import {
    FollowUpActionType,
    IncompleteFollowUpActionType,
    IncompleteFollowUpActionSchema,
    ZenputFileType
} from './FollowUpActions.types';
import { FUAReplies } from './ShowFollowUpAction';

const useStyles = makeStyles()((theme) => {
    const { spacing, typography } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(10),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: typography.body2.fontSize,
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

interface EditSavedFollowUpActionProps {
    followUpAction: FollowUpActionType;
    getStorageFullUrl: (value: string) => string;
    onGoBack: () => void;
    open: boolean;
    updateFollowUpActionSuccess: (followUpAction: FollowUpActionType) => void;
    uploadFollowUpFiles: (files: ZenputFileType[]) => string[];
}

export const EditSavedFollowUpAction = (props: EditSavedFollowUpActionProps) => {
    const { followUpAction, getStorageFullUrl, onGoBack, open, updateFollowUpActionSuccess, uploadFollowUpFiles } =
        props;
    const { classes } = useStyles();

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useMuiForm<IncompleteFollowUpActionType>({
        resolver: zodResolver(IncompleteFollowUpActionSchema),
        defaultValues: {
            description: followUpAction.data.description,
            due_date: toDate(followUpAction.date_due),
            files: [],
            require_photo: followUpAction.reply_type === FUAReplies.PHOTO,
            title: followUpAction.title
        }
    });

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : {};
    };

    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: dueDateField } = useMuiController({ control, name: 'due_date' });
    const { field: filesField } = useMuiController({ control, name: 'files' });
    const { field: requirePhotoField } = useMuiController({ control, name: 'require_photo' });
    const { field: titleField } = useMuiController({ control, name: 'title' });

    const [s3keys, setS3Keys] = useState(followUpAction.data.images);
    const [pendingS3keys, setPendingS3Keys] = useState<string[]>([]);

    const onSubmit = handleSubmit(async (values) => {
        const files = [...pendingS3keys, ...s3keys];

        followUpAction.date_due = values.due_date;
        if (values.require_photo) {
            followUpAction.reply_type = FUAReplies.PHOTO;
        } else {
            followUpAction.reply_type = FUAReplies.BASIC;
        }
        followUpAction.title = values.title;
        followUpAction.data = {
            description: values.description,
            images: files,
            reply: {}
        };

        await updateFollowUpActionSuccess(followUpAction);
    });

    const handleDeleteS3Keys = (s3Key) => {
        setS3Keys((prevImages) => prevImages.filter((key) => key !== s3Key));
    };

    const handlePendingAddS3Keys = (keys: string[]) => {
        setPendingS3Keys((prev) => prev.concat(keys));
    };
    const getToday = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return today;
    };

    const handleDeletePendingS3Keys = (indexToRemove) => {
        setPendingS3Keys((prevKeys) => prevKeys.filter((_, index) => index !== indexToRemove));
    };

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onGoBack}
            slotProps={{
                paper: {
                    sx: {
                        overflow: 'hidden'
                    }
                }
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <PageLayout>
                    {{
                        appBar: (
                            <AppBar
                                onGoBack={onGoBack}
                                title={_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                            />
                        ),
                        content: (
                            <Container component="div" className={classes.formContainer}>
                                <Stack component="form" onSubmit={onSubmit}>
                                    <FollowUpActionSavedGallery
                                        getStorageFullUrl={getStorageFullUrl}
                                        handleDeletePendingS3Keys={handleDeletePendingS3Keys}
                                        handleDeleteS3Keys={handleDeleteS3Keys}
                                        handlePendingAddS3Keys={handlePendingAddS3Keys}
                                        s3keys={s3keys}
                                        uploadFollowUpFiles={uploadFollowUpFiles}
                                        {...filesField}
                                    />

                                    <TextField
                                        className={classes.formInput}
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                        required
                                        variant="filled"
                                        {...titleField}
                                        {...getError(errors.title)}
                                    />

                                    <TextField
                                        className={classes.textFieldInput}
                                        data-testid="controlled-input2"
                                        error={getError(errors.description).error}
                                        multiline
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                        required
                                        variant="filled"
                                        {...descriptionField}
                                    />
                                    {errors.description && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.description).helperText}
                                        </FormHelperText>
                                    )}

                                    <FormControlLabel
                                        sx={{ padding: 1 }}
                                        control={
                                            <Checkbox
                                                checked={requirePhotoField.value}
                                                sx={{ marginLeft: 1 }}
                                                {...requirePhotoField}
                                            />
                                        }
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                    />
                                    <MobileDatePicker
                                        className={classes.dateFieldInput}
                                        label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                        minDate={getToday()}
                                        slotProps={{
                                            textField: {
                                                variant: 'filled',
                                                slotProps: {
                                                    input: { endAdornment: <CalendarTodayIcon /> }
                                                },
                                                required: true,
                                                error: getError(errors.due_date).error
                                            }
                                        }}
                                        value={dueDateField.value ?? null}
                                        onChange={(newDate) => {
                                            dueDateField.onChange(newDate ? endOfDay(newDate) : null);
                                        }}
                                    />
                                    {errors.due_date && (
                                        <FormHelperText sx={{ marginLeft: 2 }} error>
                                            {getError(errors.due_date).helperText}
                                        </FormHelperText>
                                    )}
                                </Stack>
                            </Container>
                        ),
                        bottomBar: (
                            <BottomBar>
                                <div className={classes.bottomBar}>
                                    <Button
                                        className={classes.bottomButton}
                                        color="primary"
                                        data-testid="addFollowUpButton"
                                        onClick={onSubmit}
                                        variant="contained"
                                    >
                                        {_t('mobile_mobileform_add_follow_up_actions_forms_update_button')}
                                    </Button>
                                </div>
                            </BottomBar>
                        )
                    }}
                </PageLayout>
            </LocalizationProvider>
        </Dialog>
    );
};
